import { extend } from "@dhx/ts-common/core";
import { create, el, sv } from "@dhx/ts-common/dom";
import { EventSystem } from "@dhx/ts-common/events";
import { locate } from "@dhx/ts-common/html";
import { ScrollView } from "@dhx/ts-common/scrollView";
import { View, toViewLike } from "@dhx/ts-common/view";
import { IHandlers } from "@dhx/ts-common/types";
import { DataCollection, DataEvents } from "@dhx/ts-data";
import { Layout } from "@dhx/ts-layout";
import { Position, tooltip } from "@dhx/ts-message";
import { ItemType, Toolbar, NavigationBarEvents } from "@dhx/ts-toolbar";

import locale from "./locales/en";
import {FileStatus, IFileWrapper, IUploader, IVault, IVaultConfig, IVaultEventSystem, IVaultProgressData, ProgressBarEvents, UploaderEvents, VaultMode } from "./types";
import { Uploader } from "./Uploader";

import { layoutConfig, layoutConfigWithoutTopbar } from "./configs";
import { calculateCover, getBasis, getFileClassName, isImage, truncateWord } from "./helper";
import { ProgressBar } from "./ProgressBar";
import { ReadStackPreview } from "./ReadStackPreview";


export class Vault extends View implements IVault {
	public config: IVaultConfig;

	public data: DataCollection<IFileWrapper>;
	public events: IVaultEventSystem;

	public uploader: IUploader;

	public toolbar: Toolbar;

	private _layout: Layout;
	private _handlers: IHandlers;
	private _progressBar: ProgressBar<IVaultProgressData>;

	private _vaultView: any;

	private _readStack: ReadStackPreview;

	private _canDrop: boolean;

	private _scrollView: ScrollView;

	constructor(container: HTMLElement | string, config: IVaultConfig = {}) {
		super(null, extend({
			mode: VaultMode.list,
			toolbar: true,
			updateFromResponse: true,
			scaleFactor: 4,
			customScroll: true,
			uploader: {},
			progressBar: {}
		}, config));

		if (!this.config.toolbar) {
			this.config.uploader.autosend = true;
		}

		if (config.data){
			this.data = config.data;
			this.events = config.data.events as IVaultEventSystem;
			this.events.context = this;
		} else {
			this.events = new EventSystem(this);
			this.data = new DataCollection<IFileWrapper>({}, this.events);
		}
		this.data.config.init = obj => {
			obj.status = obj.status || FileStatus.uploaded;
			if (obj.file) {
				obj.size = obj.file.size;
				obj.name = obj.file.name;
			} else {
				obj.size = obj.size || 0;
				obj.name = obj.name || "";
			}
			if (this.config.mode === VaultMode.grid && obj.file && isImage(obj)) {
				this._readStack.add(obj, this.uploader.config.autosend);
			}
			return obj;
		};



		this._readStack = new ReadStackPreview(this.data);

		this.uploader = new Uploader(this.config.uploader, this.data, this.events);

		this._scrollView = new ScrollView(() => this._vaultView.getRootView());

		this._progressBar = new ProgressBar(this.events, this.config.progressBar);
		this.events.on(UploaderEvents.uploadProgress,
			(progress, current, total) => this._progressBar.setState(progress, { current, total }));

		this._initHandlers();

		this._initUI(container);

		this._initEvents();
	}
	destructor() {
		this.toolbar.destructor();
		this._readStack.stop();
		this.uploader.unlinkDropArea();
		this.uploader.abort();
	}
	getRootView() {
		return this._layout.getRootView();
	}
	private _initUI(container) {
		const cfg = this.config.toolbar ? layoutConfig : layoutConfigWithoutTopbar;
		(cfg as any).on = this._getDragEvents();

		const layout = this._layout = new Layout(container, cfg);

		const toolbar = this.toolbar = new Toolbar(null, { css: "vault-toolbar" });
		this.toolbar.data.parse([
			{
				id: "add",
				tooltip: locale.add,
				type: ItemType.button,
				icon: "dxi-plus"
			},
			{
				id: "upload",
				tooltip: locale.upload,
				type: ItemType.button,
				icon: "dxi icon-upload"								// Custom Web Font Icon
			},
			{
				id:"spacer",
				type: ItemType.spacer
			},
			{
				id: "remove-all",
				tooltip: locale.clearAll,
				type: ItemType.button,
				icon: "dxi-delete-forever"
			}
		]);
		this._hideUploadAndDeleteButtons();

		this._vaultView = toViewLike(create({ render: () => this._draw() }));
		if (this.config.toolbar) {
			layout.getCell("topbar").attach(toolbar);
		}
		layout.getCell("vault").attach(this._vaultView);
	}
	private _initHandlers() {
		this._handlers = {
			onclick: {
				".action-add": () => this.uploader.selectFile(),
				".action-remove-file": e => {
					const id = locate(e);
					if (!id) {
						return;
					}

					this.data.update(id, { $toRemove: true });

					setTimeout(() => {
						this.data.update(id, {$toRemove: false}, true);
						this.data.remove(id);
					}, 200);
				}
			},
			onmouseover: {
				".action-download": e => {
					tooltip(locale.download, {
						node: e.target,
						position: Position.bottom
					});
				},
				".action-remove-file": e => {
					tooltip(locale.clear, {
						node: e.target,
						position: Position.bottom
					});
				},
				".title-content, .dhx-file-name": e => {
					const id = locate(e);
					const item = this.data.getItem(id);
					tooltip(item.name, {
						node: e.target,
						position: Position.bottom,
						css: "tooltip-light"
					});
				}
			}
		};
	}
	private _getDragEvents() {
		const rect = {
			left: null,
			top: null,
			width: null,
			height: null
		};

		return {
			dragleave: e => {
				if (!this._canDrop) {
					return;
				}
				if (e.pageX > rect.left + rect.width - 1 || e.pageX < rect.left || e.pageY > rect.top + rect.height - 1 || e.pageY < rect.top) {
					this._canDrop = false;
					if (this.config.toolbar) {
						this._layout.getCell("topbar").show();
					}
					this._layout.config.css = "vault-layout";
					this._layout.paint();
				}
			},
			dragenter: (e: DragEvent) => {
				e.preventDefault();

				if (this.uploader.isActive || this._canDrop) {
					return;
				}

				const types = e.dataTransfer.types;
				for (const type of types) {
					if (type !== "Files" && type !== "application/x-moz-file") {
						this._canDrop = false;
						return;
					}
				}

				this._canDrop = true;

				const clientRect = this.getRootView().node.el.getBoundingClientRect();

				rect.left = clientRect.left + window.pageXOffset;
				rect.top = clientRect.top + window.pageYOffset;
				rect.width = clientRect.width;
				rect.height = clientRect.height;

				this._canDrop = true;
				if (this.config.toolbar) {
					this._layout.getCell("topbar").hide();
				}

				this._layout.config.css = "vault-layout dhx-dragin";
				this._layout.paint();
			},
			dragover: e => {
				e.preventDefault();
			},
			drop: e => {
				e.preventDefault();

				if (!this._canDrop) {
					return;
				}

				const dataTransfer = e.dataTransfer;
				this.uploader.parseFiles(dataTransfer);
				this._canDrop = false;
				if (this.config.toolbar) {
					this._layout.getCell("topbar").show();
				}
				this._layout.config.css = "vault-layout";
				this._layout.paint();
			}
		};
	}
	private _hideUploadAndDeleteButtons() {
		this.toolbar.hide(["upload", "remove-all"]);
	}
	private _showUploadAndDeleteButtons() {
		if (this.uploader.config.autosend) {
			this.toolbar.show("remove-all");
		} else {
			this.toolbar.show(["upload", "remove-all"]);
		}
	}
	private _initEvents() {
		this.data.events.on(DataEvents.change, () => {
			if (!this.data.getLength()) {
				this._hideUploadAndDeleteButtons();
			} else {
				this._showUploadAndDeleteButtons();
			}
			this._vaultView.paint();
		});
		this.events.on(UploaderEvents.uploadBegin, () => {
			if (this.config.toolbar) {
				this._layout.getCell("topbar").attach(this._progressBar);
			}
		});

		this.events.on(UploaderEvents.uploadComplete, () => {
			if (this.config.mode === VaultMode.grid && this.uploader.config.autosend) {
				this._readStack.read();
			}
			if (this.config.toolbar) {
				this._layout.getCell("topbar").attach(this.toolbar);
			}
		});

		this.toolbar.events.on(NavigationBarEvents.click, id => {
			switch (id) {
				case "add":
					this.uploader.selectFile();
					break;
				case "remove-all":
					this.data.removeAll();
					break;
				case "upload":
					this.uploader.send();
					break;
			}
		});

		this.events.on(ProgressBarEvents.cancel, () => {
			this.uploader.abort();
			this._vaultView.paint();
		});
	}
	private _draw() {
		const isEmpty = !this.data.getLength();
		const files = this.config.mode === VaultMode.grid ? this._drawGrid() : this._drawList();
		return el("div", {
			class: "vault dhx_widget" + (this._canDrop ? " drop-here" : ""),
			...this._handlers,
			dhx_widget_id: this._uid
		}, [
				this._canDrop || isEmpty ? this._drawDropableArea() :
					this.config.customScroll ? this._scrollView.render(files) : files
			]);
	}
	private _getFileActions(file) {
		const defaultActions = [];
		const hoverActions = [];

		const actions = [
			el(".dhx-default-actions", defaultActions),
			el(".dhx-hover-actions", hoverActions)
		];

		if (file.status === FileStatus.inprogress) {
			return actions;
		}
		if (file.status !== FileStatus.failed && file.link) {
			const link = (this.config.downloadURL || "") + file.link;
			const downloadName = link.split("/").pop().split("?")[0];

			const download = el("a", {
				download: downloadName,
				class:"download-link",
				href: link
			}, [
				el(".icon-btn.dxi.dxi-download.action-download")
			]);
			hoverActions.push(download);
		}

		const remove = el(".icon-btn.dxi.dxi-delete-forever.action-remove-file");
		hoverActions.push(remove);

		if (file.status === FileStatus.failed) {
			const warn = el(".dxi.dxi-alert-circle.warning-status");
			defaultActions.push(warn);
		}
		if (file.status === FileStatus.uploaded) {
			const uploadComplete = el(".dxi.dxi-checkbox-marked-circle.uploaded-status");
			defaultActions.push(uploadComplete);
		}

		return actions;
	}
	private _drawList() {

		return el(".dhx-files-block.dhx-webkit-scroll", this.data.map(item => {
			const isError = item.status === FileStatus.failed && item.request;
			const inProgress = item.status === FileStatus.inprogress;
			const inQueue = item.status === FileStatus.queue;
			const notUploaded = item.status !== FileStatus.uploaded;

			return el("div", {
				class: "dhx-file-item" + (item.$toRemove ? " to-remove" : "") + (inQueue ? " in-queue" : ""),
				dhx_id: item.id,
				_key: item.id
			}, [
					el(".dhx-file-icon", [
						el("div", {
							class: "dhx-file-type " + getFileClassName(item) + (notUploaded ? " not-loaded" : "")
						})
					]),

					el(".dhx-file-title", [
						el(".dhx-title-content", item.name),
						el(".dhx-file-info", [
							isError && el(".warn-message", item.request.statusText || locale.error),
							inProgress ? el(".progress-value", (item.progress * 100).toFixed(1) + "%")
								: el(".dhx-size" + (isError && ".dhx-size-error" || ""), getBasis(item.size))
						])
					]),
					inProgress && el(".dhx-download-progress", {
						style: {
							width: (item.progress * 100).toFixed(1) + "%"
						}
					}),
					!inProgress && el(".dhx-file-action", this._getFileActions(item))
				]);
		}));
	}
	private _drawDropableArea() {
		return el(".dhx-dropable-area.drop-files-here", [
			el(".dhx-big-icon-block", [
				el(".dxi.icon-upload") // Custom Web Font Icon
			]),
			!this._canDrop && el(".drop-area-bold-text", locale.dragAndDrop),
			!this._canDrop && el(".drop-area-bold-text", locale.filesOrFoldersHere),
			!this._canDrop && el(".drop-area-light-text", locale.or),
			!this._canDrop && el("button.dhx_btn.dhx_btn--flat.dhx_btn--small.action-add", locale.browse)
		]);
	}
	private _drawGrid() {
		return el("div", {
			class: "dhx-files-grid dhx-webkit-scroll"
		}, [
				el(".dhx-grid-content", this.data.map(item => {
					const inProgress = item.status === FileStatus.inprogress;
					const inQueue = item.status === FileStatus.queue;
					const isError = item.status === FileStatus.failed;
					return el("div", {
						class: "dhx-file-grid-item" + (inProgress ? " in-progress" : "")
							+ (item.$toRemove ? " to-remove" : "") + (inQueue ? " in-queue" : "") + (isError ? " failed" : ""),
						dhx_id: item.id,
						_key: item.id
					}, [
							el(".dhx-preview-wrapper", [
								item.preview ? el(".dhx-server-file-preview", [
									el("img", { src: item.preview })
								]) :
									item.image ? el("canvas", {
										width: 98 * this.config.scaleFactor,
										height: 98 * this.config.scaleFactor,
										_hooks: {
											didInsert: node => {
												const { dx, dy, sx, sy, sHeight, sWidth } = calculateCover(item.image);
												const ctx = node.el.getContext("2d");
												ctx.drawImage(item.image, sx, sy, sWidth, sHeight, dx, dy, 98 * this.config.scaleFactor, 98 * this.config.scaleFactor);
											}
										}
									}) : el("div", {
										class: "dhx-file-preview dhx-file-type " + getFileClassName(item)
									}),
								inProgress && this._drawCircle(item.progress),
								...this._getFileActions(item),
								el(".dhx-file-info", [
									isError && el(".warn-message", item.request.statusText || locale.error),
									!inProgress && el(".dhx-size" + (isError && ".dhx-size-error" || ""), getBasis(item.size))
								])

							]),
							el(".dhx-file-name", truncateWord(item.name))
						]);
				}))
			]

		);
	}

	private _drawCircle(progress) {

		return el(".progress-layout", [
			el(".progress-amount", (progress * 100).toFixed(1) + "%"),
			sv("svg", {
				xmlns: "http://www.w3.org/2000/svg",
				class: "progress-circle",
				viewBox: "0 0 60 60",
			}, [
					sv("circle", {
						"cx": 30,
						"cy": 30,
						"r": 28,
						"stroke-width": 4,
						"class": "progress-bar-background",
					}),

					sv("circle.active-circle", {
						"cx": 30,
						"cy": 30,
						"r": 28,
						"stroke-width": 4,
						"stroke-dasharray": "175.9 175.9",
						"stroke-dashoffset": (1 - progress) * 175.9,
						"class": "progress-bar-active",
					}),
				])
		]);
	}
}

